import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, X, Pencil, Wrench } from "lucide-react";
import api from "@/lib/api";

const UNITS = [["sqft", "Square feet"], ["linft", "Linear feet"], ["unit", "Per unit"]];
const unitShort = { sqft: "sq ft", linft: "linear ft", unit: "ea" };
const inputCls = "w-full rounded-xl border border-border bg-muted/50 px-3 py-2.5 text-sm focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary";
const blank = { label: "", unit: "sqft", rate: 0, min: 0, mult: true };

export default function ServicesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/service-presets"); setRows(data.presets); }
    catch { toast.error("Couldn't load services"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.label.trim()) { toast.error("Enter a service name"); return; }
    setBusy(true);
    const payload = {
      label: draft.label.trim(), unit: draft.unit,
      rate: Number(draft.rate) || 0, min: Number(draft.min) || 0,
      mult: draft.unit === "unit" ? false : !!draft.mult,
    };
    try {
      if (draft.id) {
        const { data } = await api.put(`/admin/service-presets/${draft.id}`, payload);
        setRows((r) => r.map((x) => x.id === draft.id ? data.preset : x));
        toast.success("Service updated");
      } else {
        const { data } = await api.post("/admin/service-presets", payload);
        setRows((r) => [...r, data.preset]);
        toast.success("Service added");
      }
      setDraft(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/service-presets/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Service removed"); }
    catch { toast.error("Couldn't remove"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="services-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2"><Wrench className="h-5 w-5 text-primary" /><h3 className="font-heading text-lg font-bold">Services & Rates</h3></div>
          <p className="mt-1 text-xs text-muted-foreground">Add, edit or remove the services and rates shown in the invoice estimator.</p>
        </div>
        {!draft && <button data-testid="add-service-btn" onClick={() => setDraft({ ...blank })} className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:brightness-110"><Plus className="h-4 w-4" /> Add Service</button>}
      </div>

      {draft && (
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5" data-testid="service-editor">
          <h4 className="font-heading font-bold">{draft.id ? "Edit Service" : "New Service"}</h4>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold">Service name</label>
              <input data-testid="service-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. Patio Cleaning" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold">Unit</label>
              <select data-testid="service-unit" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} className={inputCls}>
                {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold">Rate ($ per {unitShort[draft.unit]})</label>
              <input data-testid="service-rate" type="number" min="0" step="0.01" value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold">Minimum charge ($)</label>
              <input data-testid="service-min" type="number" min="0" step="1" value={draft.min} onChange={(e) => setDraft({ ...draft, min: e.target.value })} className={inputCls} />
            </div>
            {draft.unit !== "unit" && (
              <label className="flex items-center gap-2 pt-6 text-sm font-semibold">
                <input data-testid="service-mult" type="checkbox" checked={!!draft.mult} onChange={(e) => setDraft({ ...draft, mult: e.target.checked })} className="h-4 w-4 accent-primary" />
                Apply condition multiplier
              </label>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="service-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-secondary px-5 py-2.5 text-sm font-extrabold text-secondary-foreground hover:brightness-110 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
            <button data-testid="service-cancel" onClick={() => setDraft(null)} className="inline-flex items-center gap-2 rounded-xl border border-border px-5 py-2.5 text-sm font-semibold hover:bg-accent/40"><X className="h-4 w-4" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr><th className="px-4 py-3">Service</th><th className="px-4 py-3">Unit</th><th className="px-4 py-3">Rate</th><th className="px-4 py-3">Min</th><th className="px-4 py-3">Condition</th><th className="px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No services yet.</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} data-testid={`service-row-${s.id}`} className="border-t border-border/50">
                <td className="px-4 py-3 font-semibold">{s.label}</td>
                <td className="px-4 py-3">{UNITS.find((u) => u[0] === s.unit)?.[1] || s.unit}</td>
                <td className="px-4 py-3">${s.rate}/{unitShort[s.unit]}</td>
                <td className="px-4 py-3">{s.min ? `$${s.min}` : "—"}</td>
                <td className="px-4 py-3">{s.unit === "unit" ? "—" : (s.mult ? "Yes" : "No")}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`edit-service-${s.id}`} onClick={() => setDraft({ id: s.id, label: s.label, unit: s.unit, rate: s.rate, min: s.min, mult: s.mult })} className="rounded-md p-1.5 text-primary hover:bg-primary/10"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-service-${s.id}`} onClick={() => del(s.id)} className="rounded-md p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
